import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Tab,
  Tabs,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  FormatListBulleted as BulletsIcon,
  Description as DetailsIcon,
} from '@mui/icons-material';
import { Card, Pill, SectionHeader } from '../components/shared';
import { SAGE, CREAM, WARM } from '../theme';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';

const STATUS_PRESETS = {
  draft: 'neutral',
  conversed: 'warm',
  detailed: 'warm',
  polished: 'sage',
};

const BULLET_TYPE_LABELS = {
  resume: 'Resume',
  review: 'Review',
  promotion: 'Promotion',
  raise: 'Raise',
};

const BULLET_TYPE_PRESETS = {
  resume: 'sage',
  review: 'warm',
  promotion: 'warm',
  raise: 'warm',
};

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
      ...options.headers,
    },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Details editing
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({});
  const [savingDetails, setSavingDetails] = useState(false);

  // Title/description editing
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({ title: '', description: '' });
  const [savingMeta, setSavingMeta] = useState(false);

  // Bullet add dialog
  const [bulletDialog, setBulletDialog] = useState(false);
  const [newBullet, setNewBullet] = useState({ text: '', bullet_type: 'resume' });
  const [addingBullet, setAddingBullet] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch(`/api/profile/projects/${projectId}`);
      setProject(data);
      setParent(data.parent);
      setError(null);
    } catch (err) {
      console.error('[PROJECT] Error loading project:', err);
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // --- Details Tab ---
  const handleEditDetails = () => {
    const d = project.details || {};
    setDetailsForm({
      context_and_problem: d.context_and_problem || '',
      what_i_built: d.what_i_built || '',
      challenges_and_decisions: d.challenges_and_decisions || '',
      outcomes_and_impact: d.outcomes_and_impact || '',
      team_and_leadership: d.team_and_leadership || '',
    });
    setEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      const data = await apiFetch(`/api/profile/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify({ details: detailsForm }),
      });
      setProject(data);
      setEditingDetails(false);
    } catch (err) {
      console.error('[PROJECT] Error saving details:', err);
    } finally {
      setSavingDetails(false);
    }
  };

  // --- Meta editing ---
  const handleEditMeta = () => {
    setMetaForm({ title: project.title, description: project.description || '' });
    setEditingMeta(true);
  };

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      const data = await apiFetch(`/api/profile/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(metaForm),
      });
      setProject(data);
      setEditingMeta(false);
    } catch (err) {
      console.error('[PROJECT] Error saving meta:', err);
    } finally {
      setSavingMeta(false);
    }
  };

  // --- Bullets ---
  const handleAddBullet = async () => {
    if (!newBullet.text.trim()) return;
    setAddingBullet(true);
    try {
      await apiFetch(`/api/profile/projects/${projectId}/bullets`, {
        method: 'POST',
        body: JSON.stringify(newBullet),
      });
      await fetchProject();
      setBulletDialog(false);
      setNewBullet({ text: '', bullet_type: 'resume' });
    } catch (err) {
      console.error('[PROJECT] Error adding bullet:', err);
    } finally {
      setAddingBullet(false);
    }
  };

  const handleDeleteBullet = async (bulletId) => {
    if (!window.confirm('Delete this bullet?')) return;
    try {
      await apiFetch(`/api/profile/projects/${projectId}/bullets/${bulletId}`, {
        method: 'DELETE',
      });
      await fetchProject();
    } catch (err) {
      console.error('[PROJECT] Error deleting bullet:', err);
    }
  };

  // --- Render ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: WARM.rose, fontFamily: "'Outfit', sans-serif", mb: 2 }}>
            {error || 'Project not found'}
          </Typography>
          <Button onClick={() => navigate('/profile')} startIcon={<BackIcon />}
            sx={{ textTransform: 'none', color: SAGE[500] }}>
            Back to Profile
          </Button>
        </Card>
      </Box>
    );
  }

  const parentLabel = parent?.type === 'experience'
    ? `${parent.title} at ${parent.company}`
    : parent?.institution || '';

  const detailSections = [
    { key: 'context_and_problem', label: 'Context & Problem', placeholder: 'What was the situation? What problem were you solving?' },
    { key: 'what_i_built', label: 'What I Built', placeholder: 'What did you create, design, or implement?' },
    { key: 'challenges_and_decisions', label: 'Challenges & Decisions', placeholder: 'What hard problems did you face? What trade-offs did you make?' },
    { key: 'outcomes_and_impact', label: 'Outcomes & Impact', placeholder: 'What were the results? Quantify where possible.' },
    { key: 'team_and_leadership', label: 'Team & Leadership', placeholder: 'How did you lead, collaborate, or influence others?' },
  ];

  const renderDetailsTab = () => {
    const details = project.details || {};
    const hasContent = detailSections.some(s => details[s.key]);

    if (editingDetails) {
      return (
        <Box>
          {detailSections.map(({ key, label, placeholder }) => (
            <Box key={key} sx={{ mb: 3 }}>
              <SectionHeader sx={{ mb: 1 }}>{label}</SectionHeader>
              <TextField
                fullWidth multiline minRows={3} size="small"
                placeholder={placeholder}
                value={detailsForm[key] || ''}
                onChange={(e) => setDetailsForm({ ...detailsForm, [key]: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: "'Lora', serif",
                    fontSize: '0.9rem',
                  },
                }}
              />
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditingDetails(false)} size="small"
              sx={{ textTransform: 'none', color: SAGE[500] }}>
              Cancel
            </Button>
            <Button onClick={handleSaveDetails} variant="contained" size="small"
              disabled={savingDetails}
              startIcon={savingDetails ? <CircularProgress size={14} /> : <SaveIcon />}
              sx={{ textTransform: 'none', bgcolor: SAGE[500], '&:hover': { bgcolor: SAGE[600] } }}>
              {savingDetails ? 'Saving...' : 'Save Details'}
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button onClick={handleEditDetails} startIcon={<EditIcon />} size="small"
            sx={{ textTransform: 'none', color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
            {hasContent ? 'Edit Details' : 'Add Details'}
          </Button>
        </Box>
        {hasContent ? (
          detailSections.map(({ key, label }) => {
            const val = details[key];
            if (!val) return null;
            return (
              <Box key={key} sx={{ mb: 3 }}>
                <SectionHeader sx={{ mb: 0.5 }}>{label}</SectionHeader>
                <Typography variant="body1" sx={{ color: SAGE[800], fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {val}
                </Typography>
              </Box>
            );
          })
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <DetailsIcon sx={{ fontSize: 40, color: SAGE[200], mb: 1 }} />
            <Typography sx={{ color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem' }}>
              No details yet. Add structured narratives to capture your project story.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderConversationsTab = () => {
    const convIds = project.conversation_ids || [];
    return (
      <Box>
        {convIds.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {convIds.map((convId) => (
              <Card
                key={convId}
                onClick={() => navigate(`/enhancement/${convId}`)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  '&:hover': { borderColor: SAGE[400] },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatIcon sx={{ fontSize: 16, color: SAGE[400] }} />
                  <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', color: SAGE[700] }}>
                    Conversation {convId.slice(0, 8)}...
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ChatIcon sx={{ fontSize: 40, color: SAGE[200], mb: 1 }} />
            <Typography sx={{ color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', mb: 2 }}>
              No conversations linked yet.
            </Typography>
            <Button
              variant="outlined" size="small"
              onClick={() => navigate(`/profile/enhance?projectId=${projectId}&projectTitle=${encodeURIComponent(project.title)}`)}
              sx={{
                textTransform: 'none',
                fontFamily: "'Outfit', sans-serif",
                borderColor: SAGE[300],
                color: SAGE[600],
                '&:hover': { borderColor: SAGE[500], bgcolor: SAGE[50] },
              }}
            >
              Start a Conversation
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderBulletsTab = () => {
    const bullets = project.bullets || [];
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button onClick={() => setBulletDialog(true)} startIcon={<AddIcon />} size="small"
            sx={{ textTransform: 'none', color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
            Add Bullet
          </Button>
        </Box>
        {bullets.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {bullets.map((bullet) => (
              <Card key={bullet.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Pill
                        preset={BULLET_TYPE_PRESETS[bullet.bullet_type] || 'neutral'}
                        label={BULLET_TYPE_LABELS[bullet.bullet_type] || bullet.bullet_type}
                      />
                      {bullet.status === 'polished' && (
                        <Pill preset="sage" label="polished" />
                      )}
                    </Box>
                    <Typography variant="body1" sx={{ color: SAGE[800], fontSize: '0.85rem', lineHeight: 1.6 }}>
                      {bullet.text}
                    </Typography>
                    {bullet.tags?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                        {bullet.tags.map((tag, i) => (
                          <Pill key={i} preset="neutral" label={tag}
                            sx={{ fontSize: '0.65rem', height: 20 }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <IconButton onClick={() => handleDeleteBullet(bullet.id)} size="small"
                    sx={{ color: SAGE[300], '&:hover': { color: WARM.rose } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <BulletsIcon sx={{ fontSize: 40, color: SAGE[200], mb: 1 }} />
            <Typography sx={{ color: SAGE[400], fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem' }}>
              No bullets yet. Add resume bullets, review highlights, or promotion evidence.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Back navigation */}
      <Button onClick={() => navigate('/profile')} startIcon={<BackIcon />} size="small"
        sx={{ textTransform: 'none', color: SAGE[500], fontFamily: "'Outfit', sans-serif", mb: 2 }}>
        Back to Profile
      </Button>

      {/* Project header */}
      <Card sx={{ p: 3, mb: 3 }}>
        {editingMeta ? (
          <Box>
            <TextField
              fullWidth label="Project Title" value={metaForm.title}
              onChange={(e) => setMetaForm({ ...metaForm, title: e.target.value })}
              size="small" sx={{ mb: 2 }}
            />
            <TextField
              fullWidth multiline rows={2} label="Description"
              value={metaForm.description}
              onChange={(e) => setMetaForm({ ...metaForm, description: e.target.value })}
              size="small"
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button onClick={() => setEditingMeta(false)} size="small"
                sx={{ textTransform: 'none', color: SAGE[500] }}>Cancel</Button>
              <Button onClick={handleSaveMeta} variant="contained" size="small"
                disabled={savingMeta || !metaForm.title.trim()}
                sx={{ textTransform: 'none', bgcolor: SAGE[500], '&:hover': { bgcolor: SAGE[600] } }}>
                {savingMeta ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography sx={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 600,
                  fontSize: '1.5rem',
                  color: SAGE[900],
                  mb: 0.5,
                }}>
                  {project.title}
                </Typography>
                <Typography variant="body2" sx={{ color: SAGE[500], fontSize: '0.85rem' }}>
                  {parentLabel}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Pill preset={STATUS_PRESETS[project.status] || 'neutral'} label={project.status || 'draft'} />
                <IconButton onClick={handleEditMeta} size="small" sx={{ color: SAGE[400] }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
            {project.description && (
              <Typography variant="body1" sx={{ color: SAGE[700], fontSize: '0.9rem', mt: 1.5, lineHeight: 1.6 }}>
                {project.description}
              </Typography>
            )}
            {/* Stats row */}
            <Divider sx={{ my: 2, borderColor: SAGE[100] }} />
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ChatIcon sx={{ fontSize: 14, color: SAGE[400] }} />
                <Typography sx={{ fontSize: '0.75rem', color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
                  {project.conversation_ids?.length || 0} conversations
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BulletsIcon sx={{ fontSize: 14, color: SAGE[400] }} />
                <Typography sx={{ fontSize: '0.75rem', color: SAGE[500], fontFamily: "'Outfit', sans-serif" }}>
                  {project.bullets?.length || 0} bullets
                </Typography>
              </Box>
              {project.tags?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {project.tags.map((tag, i) => (
                    <Pill key={i} preset="neutral" label={tag} sx={{ fontSize: '0.65rem', height: 20 }} />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Card>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {
            fontFamily: "'Outfit', sans-serif",
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.9rem',
            color: SAGE[400],
            '&.Mui-selected': { color: SAGE[700] },
          },
          '& .MuiTabs-indicator': { backgroundColor: SAGE[500] },
        }}
      >
        <Tab icon={<DetailsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Details" />
        <Tab icon={<ChatIcon sx={{ fontSize: 18 }} />} iconPosition="start"
          label={`Conversations (${project.conversation_ids?.length || 0})`} />
        <Tab icon={<BulletsIcon sx={{ fontSize: 18 }} />} iconPosition="start"
          label={`Bullets (${project.bullets?.length || 0})`} />
      </Tabs>

      {/* Tab content */}
      <Card sx={{ p: 3 }}>
        {activeTab === 0 && renderDetailsTab()}
        {activeTab === 1 && renderConversationsTab()}
        {activeTab === 2 && renderBulletsTab()}
      </Card>

      {/* Add Bullet Dialog */}
      <Dialog open={bulletDialog} onClose={() => !addingBullet && setBulletDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Outfit', sans-serif" }}>Add Bullet</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }} size="small">
            <InputLabel>Type</InputLabel>
            <Select value={newBullet.bullet_type} label="Type"
              onChange={(e) => setNewBullet({ ...newBullet, bullet_type: e.target.value })}>
              <MenuItem value="resume">Resume</MenuItem>
              <MenuItem value="review">Performance Review</MenuItem>
              <MenuItem value="promotion">Promotion</MenuItem>
              <MenuItem value="raise">Raise</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth multiline rows={3} label="Bullet Text"
            value={newBullet.text}
            onChange={(e) => setNewBullet({ ...newBullet, text: e.target.value })}
            disabled={addingBullet}
            placeholder="e.g., Led migration of 3 microservices to Kubernetes, reducing deployment time by 60%"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulletDialog(false)} disabled={addingBullet}
            sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleAddBullet} variant="contained"
            disabled={!newBullet.text.trim() || addingBullet}
            sx={{ textTransform: 'none', bgcolor: SAGE[500], '&:hover': { bgcolor: SAGE[600] } }}>
            {addingBullet ? 'Adding...' : 'Add Bullet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetailPage;
